import type { MutableRefObject } from 'react';
import React, { useEffect, useState } from 'react';
import { Drawer } from '@noths/polaris-client-ribbons-design-system';
import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { Icon } from '@noths/polaris-design-system-core-components';

import type { DeliveryFAQsProps } from 'src/components/molecules/DeliveryFAQs/DeliveryFAQs';
import { DeliveryFAQs } from 'src/components/molecules/DeliveryFAQs/DeliveryFAQs';
import type { DeliveryInfoProps } from 'src/components/molecules/DeliveryInfo/DeliveryInfo';
import { DeliveryInfo } from 'src/components/molecules/DeliveryInfo/DeliveryInfo';
import { DeliveryBlock } from 'src/components/organisms/DeliveryBlock/DeliveryBlock';
import type { DeliveryBlockProps } from 'src/components/organisms/DeliveryBlock/types';
import * as copy from './copy';
import * as styles from './DeliveryDrawer.styles';

export interface DeliveryDrawerViewProps {
  deliveryBlock: DeliveryBlockProps;
  /** Null hides the FAQs section, for products with no FAQ content */
  deliveryFAQs: DeliveryFAQsProps | null;
  deliveryInfo: DeliveryInfoProps;
  isOpen: boolean;
  onClose: () => void;
  /** The CTA column the drawer aligns its left edge to */
  target: MutableRefObject<HTMLDivElement | null>;
}

const isMobileDevice = () => {
  return /Mobi|Android/i.test(navigator.userAgent);
};

export const DeliveryDrawerView = ({
  deliveryBlock,
  deliveryFAQs,
  deliveryInfo,
  isOpen,
  onClose,
  target,
}: DeliveryDrawerViewProps) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      const leftPos = target.current?.getBoundingClientRect().left;
      const currentWidth = isMobileDevice()
        ? window.screen.width - leftPos!
        : window.innerWidth - leftPos!;
      setWidth(currentWidth);
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Drawer
      contentLabel={copy.DRAWER_CONTENT_LABEL}
      describedBy={copy.DRAWER_DESCRIPTOR_ID}
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <section css={styles.section} style={{ width }}>
        <header css={styles.header}>
          <button css={styles.closeButton} onClick={onClose} type="button">
            <Icon
              color={`var(${themeVariables.colors.content.interactive})`}
              name="close"
              size="md"
            />
            Close
          </button>
        </header>
        <div css={styles.content}>
          <div css={styles.datesSection}>
            <DeliveryBlock {...deliveryBlock} />
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            <p css={styles.message}>You'll pick your delivery options at checkout</p>
          </div>
          {deliveryFAQs && <DeliveryFAQs {...deliveryFAQs} />}
          <DeliveryInfo {...deliveryInfo} />
        </div>
      </section>
    </Drawer>
  );
};
